import * as React from 'react';
import { REGIONS } from '@/jurisdictions/regions';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export interface TemplateAvailabilityValue {
  availability_region: string | null;
  availability_forum: string | null;
}

const ALL_MATTERS = 'all';
const SPECIFIC_PROFILE = 'profile';
const DEFAULT_REGION = REGIONS[0]?.name ?? 'UK';
const DEFAULT_FORUM = 'Litigation';
const FORUM_OPTIONS = ['Litigation', 'Arbitration'];

export const emptyTemplateAvailability = (): TemplateAvailabilityValue => ({
  availability_region: null,
  availability_forum: null,
});

export const templateAvailabilityLabel = (availability: TemplateAvailabilityValue) => {
  if (!availability.availability_region || !availability.availability_forum) {
    return 'All matters';
  }
  return `${availability.availability_region} · ${availability.availability_forum}`;
};

export const TemplateAvailabilityFields = ({
  disabled = false,
  onChange,
  value,
}: {
  disabled?: boolean;
  onChange: (value: TemplateAvailabilityValue) => void;
  value: TemplateAvailabilityValue;
}) => {
  const mode = value.availability_region || value.availability_forum ? SPECIFIC_PROFILE : ALL_MATTERS;
  const region = value.availability_region || DEFAULT_REGION;
  const forum = value.availability_forum || DEFAULT_FORUM;

  const setSpecificProfile = React.useCallback(() => {
    onChange({ availability_region: region, availability_forum: forum });
  }, [forum, onChange, region]);

  return (
    <div className="grid gap-2">
      <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
        Availability
      </span>
      <Select
        value={mode}
        onValueChange={(nextMode) => {
          if (nextMode === ALL_MATTERS) {
            onChange(emptyTemplateAvailability());
            return;
          }
          setSpecificProfile();
        }}
        disabled={disabled}
      >
        <SelectTrigger className="h-10">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_MATTERS}>All matters</SelectItem>
          <SelectItem value={SPECIFIC_PROFILE}>Specific region and matter type</SelectItem>
        </SelectContent>
      </Select>

      {mode === SPECIFIC_PROFILE ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <Select
            value={region}
            onValueChange={(nextRegion) => onChange({
              availability_region: nextRegion,
              availability_forum: forum,
            })}
            disabled={disabled}
          >
            <SelectTrigger className="h-10">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {REGIONS.map((regionOption) => (
                <SelectItem key={regionOption.name} value={regionOption.name}>
                  {regionOption.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={forum}
            onValueChange={(nextForum) => onChange({
              availability_region: region,
              availability_forum: nextForum,
            })}
            disabled={disabled}
          >
            <SelectTrigger className="h-10">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FORUM_OPTIONS.map((forumOption) => (
                <SelectItem key={forumOption} value={forumOption}>
                  {forumOption}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : null}
    </div>
  );
};
